// The Briefing for an ongoing story.
//
//   🌐  Briefing · Buy sunglasses
//   🌐  3 research packs
//   ✔   2 tasks
//   📅  1 event
//
// What used to be a row of filter PILLS in the capsule above the box. It is the same information a
// briefing carries — what this thing is made of — so it says it the same way, in the same strip.
//
// The rows stay live: a row that names a kind IS the filter for that kind, and the active one is
// marked. Clicking is the strip's job (it dispatches `ongoing/setStoryFilters`); this module only
// says which rows exist, which is active, and what each one filters to — no closures, so the whole
// thing stays a plain value the store can hand around.

import { briefing } from './rows'
import { MATCHES_ICON } from './email'
import { moduleIcon } from '@/lib/moduleIcons'
import { packListView } from '@/lib/packFocus'
import { storyPacks } from '@/lib/storySteps'
import { kindItems } from './kindItems'
import { TEXT as EMAIL_TEXT } from '@/lib/emailSession'
import { connectionsLabel } from '@/lib/storyConnections'
import { connectionsLabel as possibleConnectionsLabel } from '@/lib/possibleConnections'
import { emailThreadCount, entityThreadCounts, threadKind } from '@/lib/storyThreads'
import { messageMeta, messageThreadCount } from '@/lib/sources'
import { selectedHeading } from '@/lib/packCompare'
import { draftBriefingText } from '@/lib/draftEvidence'
import { replyPickRows } from './replyPick'
import { attachmentsRow } from '@/lib/attachmentsBriefing'
import { aggregateRows } from './packAggregate'
import { countLabel } from './rows'
import { CONVERSATIONS_FILTER, CONVERSATIONS_ICON } from '@/lib/ongoingConversations'

// busy-my-web#609 (Saffet 2026-10-09): the shared `countLabel` — thousands separator, capital after the count.
const plural = (n, word) => countLabel(n, word)

/** The possible-relations row's key — what the strip and its unfold agree to call it (#333). */
export const RELATIONS_KEY = 'possible-relations'

/**
 * "3 possible relations" — the story's own wording for the offers.
 *
 * The email card says "3 possible Ongoing matches", because up there the open question is WHICH
 * Ongoing each mail belongs to. Inside an Ongoing that half is already answered — this one — so
 * naming it again in the row would be the story asking about itself. What is left to say is how
 * many things may relate to it, which is the words Pouria used for it.
 */
// Since Pouria's 2026-10-01 frame (40011046:161537) the row names the table it opens: "5 Possible
// connections" — the crumb writes "5 Possible conne…", the table is headed "Possible
// connections", and "possible relations" was the one place still using the older word. The
// wording is lib/possibleConnections' `connectionsLabel`, so the row, the crumb and the table's
// heading are one noun.
export function possibleRelationsLabel(count) {
  const n = Number(count) || 0
  return possibleConnectionsLabel(n) || '0 Possible connections'
}

/**
 * "2 Connections" — the knowledge graph's confirmed connections of the story (busy-my-web#710).
 * A plain count row: nothing to filter and nothing to unfold (the story's own steps already list
 * what was connected). Null at zero or with no count.
 */
export const GRAPH_CONNECTIONS_KEY = 'graph-connections'
export function graphConnectionsRow(count) {
  const n = Number(count) || 0
  if (n <= 0) return null
  return { key: GRAPH_CONNECTIONS_KEY, icon: 'link-connection', text: countLabel(n, 'Connection') }
}

/**
 * The Ongoing's possible-relations row (busy-my-web#333).
 *
 * #317 moved the mailbox's offers onto the email card and took the story's always-on "Connect this
 * email to this Ongoing" list away with it — which left an open Ongoing saying nothing at all about
 * the mail that may belong to it. This is the row that says it: the same radar the email card wears
 * (one glyph for one idea, imported rather than re-spelled), over the same offers, counted for THIS
 * story only.
 *
 * Absent at zero, like every other row in this briefing: a story with nothing pending should read as
 * calm, not as "0 possible relations".
 *
 * `storyRelations` (not `filter`) is what marks it, for the reason the email card's `relations` is
 * not a filter either: a suggestion is not a KIND of thing the story contains, so it must not touch
 * `storyFilters` — the rail, the timeline and the other rows stay exactly where they were, and what
 * the row does instead is unfold the match cards underneath itself. `ongoingId` rides along so the
 * unfold knows which story's questions to draw without asking the store a second time.
 */
export function possibleRelationsRow(count, { open = false, ongoingId = '', kinds = [], module = '' } = {}) {
  const n = Number(count) || 0
  if (n <= 0) return null
  return {
    key: RELATIONS_KEY,
    icon: MATCHES_ICON,
    text: possibleRelationsLabel(n),
    storyRelations: true,
    ongoingId: String(ongoingId || ''),
    active: !!open,
    // …and, while the table is open, WHAT the connections are, one row per module with its own
    // "View ↗" (Pouria, 2026-10-01, frame 40011046:161537: "1 Email", "1 task", "1 Note", "1 Event",
    // "1 Research packs") — lib/possibleConnections' `connectionKinds`, counted off the table's own
    // rows. `module` is the one a "View" has narrowed the table to, which that row wears as active.
    // Only while open: at rest the row is one line, as every count row in this strip is.
    ...(open && Array.isArray(kinds) && kinds.length
      ? { kinds: kinds.map(k => ({ ...k, active: !!module && k.module === module })) }
      : {}),
  }
}

// busy-my-web#146 — Saffet, 2026-08-21: "Can we remove the 1 task and and put close in front of the
// task where it writes open as it is open." An ACTIVE kind row lists its own items directly
// underneath it (BriefingBlock.vue's kind-item list), so the count above them says again exactly
// what the list below is showing — "1 task" over a list of one task.
//
// The COUNT goes and the row stays: its × is the only way back out of the filter (with a filter on,
// every other row hides), so deleting the row would trap the Briefing inside it. A row that is not
// active keeps its count — that is the whole reason to click it.
const bareCount = (text) => String(text).replace(/^([\d,]+)\s+(.+)$/, (_, n, word) => (n === '1' ? `${word}s` : word))


/**
 * What the ongoing story is made of, for the briefing above.
 *
 * COUNTS EVERYTHING, including material filed into a Research Pack — because each row is a FILTER,
 * and clicking it lists the pack-filed ones too. A count that left them out said "2 notes" and then
 * showed three, which reads as a bug in the count rather than as a rule about packs.
 *
 * That is a different question from what the middle timeline SHOWS at rest, where pack-filed
 * material stays inside its pack (see `progress` in lib/storySteps.js) — the pack's own node
 * already stands for it there. Pouria: "when we have something under the research pack it's true
 * we don't need to show it, but the count should be included."
 *
 * So the invariant is: this number equals what its own filter reveals.
 *
 * @param subs how many sub-ongoings this story has (the caller knows; the store does)
 */
// What the story's email THREADS hold, read off the loaded timelines (Pouria, 2026-08-29: "we have
// 2 emails and one task" — the strip said "1 email" because it counted the thread, not the mail
// in it, and nothing for the task that sits on that thread). A thread the store does not hold yet
// still counts as one email, as before, until it arrives.
function threadContentCounts(story, getTimeline) {
  const out = { emails: 0, tasks: 0, events: 0, notes: 0 }
  const threads = (story?.emails || []).filter(em => em && em.timelineId && threadKind(em) === 'email')
  threads.forEach((em) => {
    const tl = typeof getTimeline === 'function' ? getTimeline(em.timelineId) : null
    if (!tl) { out.emails += 1; return }
    const items = (tl.timelineItems || []).filter(it => it && !it.isDeleted)
    const messages = items.filter(it => it.itemType === 1).length || (tl.emailMessages || []).filter(m => m && m.isDeleted !== true).length
    out.emails += Math.max(1, messages)
    items.forEach((it) => {
      if (it.itemType === 2) {
        const t = (tl.tasks || []).find(x => x.id === it.id)
        const done = t && (t.status === 'done' || t.status === 2 || t.isDone === true || !!t.completedDate)
        if (!done) out.tasks += 1
      } else if (it.itemType === 3) out.events += 1
      else if (it.itemType === 4) out.notes += 1
    })
  })
  return out
}

export function countStory(story, { subs = 0, getTimeline = null } = {}) {
  const timeline = story?.timeline || []
  const onThreads = threadContentCounts(story, getTimeline)
  // Tasks and events count only while OUTSTANDING: the row is offered as work still to do, and a
  // finished task is not that.
  const open = timeline.filter(e => (e.kind === 'task' || e.kind === 'event') && !e.isDone)

  // The entities that are on the rail as their own linked THREAD, not as a step — the task an
  // ongoing was created from is the everyday case. The rail lists them under their kind's filter, so
  // the count above that filter has to include them.
  const threads = entityThreadCounts(story)

  return {
    // The packs the TIMELINE draws, not the `packs` array alone (busy-my-web#156): the rail also
    // makes a node per legacy research group, and counting only the real ones said "no packs" over
    // a timeline showing three. One rule, in lib/storySteps.js, read by both.
    packs: storyPacks(story).length,
    tasks: open.filter(e => e.kind === 'task').length + threads.task + onThreads.tasks,
    events: open.filter(e => e.kind === 'event').length + threads.event + onThreads.events,
    notes: timeline.filter(e => e.kind === 'note').length + threads.note + onThreads.notes,
    // Contacts (busy-my-web#242). A contact made inside an ongoing advances the story exactly as a
    // note does — "Contact created: …", Figma Stage 13 — but the Briefing had no row for it, so
    // Fatemeh's ongoing reported the event she had made and stayed silent about the contact beside
    // it. Steps only: a contact has no linked thread on the rail to double-count.
    contacts: timeline.filter(e => e.kind === 'contact').length,
    // Real mail only. The story's `emails` list holds every LINKED THREAD, including the one that
    // carries the very task the ongoing was created from — counting those made a brand-new task's
    // story announce "1 email" it had never been sent (Pouria, 2026-08-26).
    // …the MAIL in the story's threads, not the threads (see threadContentCounts); the thread count
    // is the floor for threads not loaded yet.
    emails: Math.max(emailThreadCount(story), onThreads.emails),
    // Message threads (busy-timeline-srv#29) — SMS/WhatsApp conversations and logged calls linked
    // to the story. Counted apart from mail: "2 emails, 1 message" is what the story is made of.
    messages: messageThreadCount(story),
    subs,
  }
}

/**
 * @param story the ongoing story being viewed
 * @param filters `ongoing/storyFilters` — `{ kind, packId }`, the one currently applied
 * @param counts what the caller has already worked out: { packs, tasks, events, notes, contacts,
 *        emails, subs }
 * @param opts `{ packs, children, picks, draft }` — the story's Research Packs, which the strip LISTS while
 *        the packs filter is on (#56), the sub-ongoings whose steps the kind lists include, and what
 *        has been ticked, and `attachments` — the story's attachment counts from
 *        `documents/briefing?scope=ongoing:<id>` (busy-my-web#277), which only the server can
 *        answer. (`canAddSub` used to be here, for the create row #220 removed; whether a
 *        story MAY spawn a child is the suggestion panel's question now, not the strip's.)
 *        `conversations` — how many kept conversations the Ongoing holds (busy-my-web#654), the
 *        server's count, pushed like the attachments.
 *        `matches` — `{ count, open }`, the offers pending for THIS Ongoing (busy-my-web#333) and
 *        whether their cards are unfolded. Pushed like the attachments above it: the count is over
 *        `ongoing/suggestions`, which is a different store from the story object this builder is
 *        handed, and a pure builder must not go looking for it.
 */
export function storyBriefing(story, filters, counts = {}, opts = {}) {
  if (!story) return null

  const active = filters?.kind || null
  const rows = []
  // A row per kind the story actually contains — a "0 tasks" filter would filter to nothing.
  const row = (kind, icon, text, count) => {
    if (!count) return
    // The research row stays lit while a pack is SELECTED, whatever kind is narrowing it — the
    // pack filter is still on, and the row going dark would say otherwise.
    const on = active === kind || (kind === 'research' && !!filters?.packId)
    // `full` keeps the counted wording for the breadcrumb, which names this depth as a place
    // rather than as the header of the list under it (busy-my-web#188).
    rows.push({ key: kind, icon, text: on ? bareCount(text) : text, full: text, filter: kind, active: on })
  }

  // The email groups this story is CONNECTED to, first — frame 40007198:182698 heads the briefing
  // of a story made from email groups with them (busy-my-web#104).
  //
  // It IS a filter, like the count rows below it (busy-my-web#105, frame 40008461:190811): clicking
  // it turns the row into the frames' closeable chip and lists the connections in the MIDDLE, under
  // the story — where they can be ticked and removed. What it does not do is narrow the story's own
  // steps: a connection is a sender the ongoing follows, not a kind of thing inside it, so the
  // middle's timeline and the rail stay exactly where they were (`storyFilters` is untouched).
  const connections = (opts.connections || []).filter(Boolean)
  if (connections.length) {
    rows.push({
      key: 'email-connections',
      icon: 'sms-tracking',
      text: connectionsLabel(connections.length),
      filter: 'connections',
      active: !!opts.connectionsOpen,
    })
  }

  // "3 Research packs" — the frame (40007883:371175) writes it lower case, but every Briefing row
  // capitalises the word after its count since busy-my-web#609 (Saffet 2026-10-09); it is now the
  // very words of the middle's "Research packs" heading.
  row('research', 'research-pack', plural(counts.packs, 'research pack'), counts.packs)
  // …and, directly under it, WHAT THOSE PACKS HOLD — the Ongoing-level aggregate (busy-my-web#441,
  // Saffet: "When you click the Briefing you should see 2 Research Packs / 8 Extractions /
  // 4 Photos / 1 Product"; Pouria, 2026-09-15: the Extractions and Photos rows belong to the pack,
  // not here). Only the kinds the story's own rows below do not already count; lib/briefings/packAggregate.js owns both that rule and the arithmetic, so
  // the aggregate here and the count rows under a single pack are the same answer at two depths.
  // Only while the packs row itself is at rest: with the research filter ON, the strip is listing
  // the packs themselves and their own counts are the more immediate answer.
  if (counts.packs && active !== 'research') rows.push(...aggregateRows(story))
  row('task', 'tick-circle', plural(counts.tasks, 'task'), counts.tasks)
  row('event', 'events', plural(counts.events, 'event'), counts.events)
  row('note', 'notes', plural(counts.notes, 'note'), counts.notes)
  // …and the contacts made inside the story (busy-my-web#242), in the module's own glyph — the row
  // filters to them and lists them exactly as every other kind does, and each one opens in the box
  // (lib/storySteps.js's `editOf` builds a contact's opener since the same issue).
  row('contact', moduleIcon('contact'), plural(counts.contacts, 'contact'), counts.contacts)
  row('email', 'email', plural(counts.emails, 'email'), counts.emails)
  // "N messages" (busy-timeline-srv#29) in the messaging module's glyph; the row filters the rail
  // to the message-thread nodes, each of which is stepKind 'message'.
  row('message', messageMeta().icon, plural(counts.messages || 0, 'message'), counts.messages || 0)
  // "3 Conversations" (busy-my-web#654) — the Ongoing's kept chats, its own and the ones saved here
  // from Home. Counted by the server like the attachments below (`opts.conversations`), and listed
  // by its own panel under the row (SmartBox/ConversationsPanel.vue), each opening that conversation.
  // Conversations, not questions: one conversation holds many (Pouria, 2026-10-21).
  row(CONVERSATIONS_FILTER, CONVERSATIONS_ICON, plural(opts.conversations || 0, 'conversation'), opts.conversations || 0)

  // The story's ATTACHMENTS (busy-my-web#277) — the files on its threads, counted by the server
  // because only it knows which threads the story covers and what each file IS. Not part of
  // `countStory`: every other row here is counted off the story object the client already holds, and
  // this one cannot be, so it arrives through `opts` the way the packs and the connections do. A
  // story with no files says nothing about them, like every other row.
  const attachments = attachmentsRow(opts.attachments, { active: active === 'attachments' })
  if (attachments) rows.push(attachments)

  row('sub', 'story', plural(counts.subs, 'sub-ongoing'), counts.subs)

  // …and, last of the block, the one line that is not a count of what the story HAS: the emails that
  // may belong to it and have not been answered yet (busy-my-web#333). It sits under the counts
  // because that is the reading order of the strip — what this is, then what it might also be — and
  // it unfolds its own cards rather than filtering anything (see `possibleRelationsRow`).
  // …and, just above it, how many things are CONFIRMED as connected to this story in the knowledge
  // graph (busy-my-web#710, frame 40015353:452286 "1 Connection"), off the BFF's
  // `v1/connections/summary/ongoing/<id>`. Absent while unknown (an old BFF) and at zero.
  // The "N Connections" row (busy-my-web#710) is no longer drawn (Pouria, 2026-11-03: "remove
  // these connections on the UI") — the confirmed links are already the story's own steps, and a
  // bare count with nothing to open read as a dead row. `graphConnectionsRow` stays for its tests
  // and any later surface; `opts.graphConnected` is ignored here.

  const relations = possibleRelationsRow(opts.matches?.count, {
    open: opts.matches?.open,
    ongoingId: story.id,
    kinds: opts.matches?.kinds,
    module: opts.matches?.module,
  })
  if (relations) rows.push(relations)

  // …and the promise the create step made, restated where the story now lives (frame's third row).
  // (The frame draws this line without an icon; `briefing()` gives every row one, so it takes the
  // same link glyph the create step's own "future emails" row wears rather than the generic globe.)
  // What it PROMISES changed with busy-timeline-srv#21. With
  // auto-connect off (the default) future mail from these people is SUGGESTED, not linked; with the
  // story's switch on, the original sentence is true again and is the one shown.
  if (connections.length) {
    rows.push({
      key: 'email-future',
      icon: 'link-connection',
      text: opts.autoConnect ? EMAIL_TEXT.futureAppearAuto : EMAIL_TEXT.futureAppear,
    })
  }

  // (There is no "New sub-ongoing" row here any more — busy-my-web#220. The Briefing REPORTS what a
  // story is made of; every row above it is a count of something that exists. The create row was the
  // one row that made something, and Saffet ruled it out of the strip entirely on 2026-08-26: "no
  // 'New sub Ongoing' create row in the Briefing at all — creating one stays a suggestion offered at
  // the right moment". Where the story HAS sub-ongoings, the `sub` row above counts them and lists
  // them, each opening its own child story; where it has none, the Briefing says nothing about them,
  // which is the truth. The journey itself is untouched — lib/subOngoing.js and SubOngoingPanel's
  // "What would you like to do?" panel — it is only the strip that no longer offers it.)

  // …and, while a drafted reply is sitting in the composer, what it was written from
  // (busy-ai-srv#13, spec §8): "Draft based on: <thread> · 3 sources". The Briefing reports what is
  // in hand, and right now what is in hand is a reply and its evidence — the Work-board lists the
  // sources themselves, this line is the one sentence that says a draft exists at all. It is a
  // FACT, not a filter: clicking it narrows nothing, because a draft is not a kind of thing the
  // story contains.
  // …and, when the reply was asked for in words ("reply to the supplier"), WHICH thread the Brain
  // picked and why (busy-main-bff#25, spec §8) — above the "Draft based on:" line, because the
  // thread is the choice that was made FOR the user and the sources are what was read once it was
  // made. "Switch thread" follows it as an action row whenever the story had another thread it could
  // have been: a pick the user can see but not correct is only half an explanation.
  // Built by lib/briefings/replyPick.js rather than here, because the composer's own briefing has
  // to state the same pick in the same words once the reply gesture has navigated away from the
  // story (busy-main-bff#25 — see that module's note).
  rows.push(...replyPickRows(opts.replyPick || null))

  const draftRow = draftBriefingText(opts.draft || null)
  if (draftRow) rows.push({ key: 'draft', icon: 'draft', text: draftRow })

  // No rows and nothing to say — except when a connection has just been REMOVED (#105): an email
  // story whose last connection has gone can have no rows left at all, and the strip disappearing
  // would take the "Email connection removed." receipt with it. The briefing stays, titled, until
  // the receipt is dismissed.
  if (!rows.length && !opts.notice) return null

  // What the user has TICKED, named under the rows (frame 40007883:371467). The packs are on the
  // Work-board by then, but the Briefing is what says in words what is selected — "the briefing
  // says what has been selected, and the user can open them from the Briefing, the Work-board or
  // the Timeline" (Fatemeh, step 3). Same picks the Work-board chips and the compare ask read, so
  // the three cannot disagree about what is in hand.
  const selected = (opts.picks || [])
    .filter(p => p && p.id)
    .map(p => ({ id: String(p.id), title: String(p.title || 'Research pack') }))

  // With the packs filter on, the row alone ("2 Research Packs") no longer says enough: Saffet asked
  // to SEE the packs there, each openable. So the strip carries them under that row — the list is
  // part of the briefing, not a second control bolted beside it. Once anything is SELECTED the list
  // goes away again (Pouria, 2026-08-16, frame 40007883:371467) — a pack selected in the strip
  // (filters.packId) or ticked on a card (the picks above), the two gestures read the same: the
  // strip then says total → Selected — the "N research packs" row plus the packs named under
  // "Selected" — and the full list would only repeat what those two already say.
  const packs = active === 'research' && !filters?.packId && !selected.length
    ? packListView(opts.packs, null)
    : null

  // Every OTHER kind gets the same treatment on select (Pouria, 2026-08-15): its items listed
  // under the row — name-first titles straight from the story's own steps, each one a jump to
  // that step on the right rail. Built only for the kind in force, so the strip at rest carries
  // no lists at all.
  // (Attachments are the one filter with no step behind it — a file is not a step on the story's
  // timeline, it hangs off a thread — so it lists itself, from the server's own document list, in
  // AttachmentsPanel.vue. Asking `kindItems` for them would hand the strip an empty list to draw.)
  const items = active && active !== 'research' && active !== 'attachments' && active !== CONVERSATIONS_FILTER
    ? kindItems(story, opts.children, active)
    : null

  return briefing({
    kind: 'story',
    // "Briefing · <name>", the same separator the email, task and entity briefings use and the one
    // the frames write (40007883:370830 stage 1). The story briefing was the last one still using an
    // em dash, which is the line Pouria pointed at on 2026-08-26.
    title: `Briefing · ${story.title || 'Ongoing'}`,
    titleIcon: 'story',
    rows,
    packs,
    kindItems: items,
    selected: selected.length ? selected : null,
    // "2 Research packs selected", not a bare "Selected" (#257, frame 40008185:406044) — see
    // lib/packCompare.js's selectedHeading for why the count and the type are both in the line.
    selectedLabel: selected.length ? selectedHeading(selected.length) : '',
  })
}
